/**
 * Application Provider Stack
 * Wraps children with all necessary context providers in the correct order.
 * Order matters: outer providers must not depend on inner providers.
 */

import React from 'react'

import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'

import { ImportAttributeLabelProvider } from '@/modules/accounting/features/import/hooks/useImportAttributeLabel'
import { queryClient } from '@/platform/query-client'
import { StripeProvider } from '@/platform/stripe'
import { NativeValidationTranslator } from '@/shared/components/NativeValidationTranslator'
import { NotificationProvider } from '@/shared/components/Notification'
import { AuthProvider } from '@/shared/contexts/AuthContext'
import { DeletionWarningProvider } from '@/shared/contexts/DeletionWarningContext'
import { ImpersonationProvider } from '@/shared/contexts/ImpersonationContext'
import { ThemeProvider } from '@/shared/theme'
import { UI_TIMINGS } from '@/shared/utils/constants'

/**
 * Toast styling matches the crypto-dark theme (BUG-66). The previous build
 * had ~30 call-sites importing `toast` from `react-hot-toast` but no
 * `<Toaster />` was ever mounted — so every `toast.success()` was a no-op
 * and the UI gave no feedback after create/update/delete operations.
 */
const TOAST_OPTIONS = {
  duration: UI_TIMINGS.TOAST_SUCCESS,
  style: {
    background: '#1a1a2e',
    color: '#e5e7eb',
    border: '1px solid #2a2a3e',
    borderRadius: '0.5rem',
    padding: '12px 16px',
    fontSize: '14px',
  },
  success: {
    iconTheme: {
      primary: '#10b981',
      secondary: '#1a1a2e',
    },
  },
  error: {
    duration: UI_TIMINGS.TOAST_ERROR,
    iconTheme: {
      primary: '#ef4444',
      secondary: '#1a1a2e',
    },
  },
}

export const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <QueryClientProvider client={queryClient}>
    {/* top-right, NOT bottom-right: the bottom-right corner is occupied by the
        fixed `ImportProgressIndicator`, which the toast stack (z-[9999]) sat on
        top of. It also puts these toasts in the same corner as the
        react-hot-toast `<Toaster>` below, so the app has one toast area. */}
    <NotificationProvider position="top-right" maxNotifications={4}>
      <ThemeProvider>
        <AuthProvider>
          <DeletionWarningProvider>
            <ImpersonationProvider>
              <StripeProvider>
                {/* El catálogo VIVO de atributos, para que toda superficie de
                    importación nombre igual un atributo creado en
                    `/admin/system/attributes`. Va AQUÍ y no en un layout: el
                    panel de super-admin cuelga de `SuperAdminLayout`, un
                    armazón hermano, y montarlo en `AuthenticatedLayout` dejó
                    fuera justo la pantalla que medía el defecto. Sin sesión no
                    pide nada. Ver `useImportAttributeLabel`. */}
                <ImportAttributeLabelProvider>{children}</ImportAttributeLabelProvider>
              </StripeProvider>
            </ImpersonationProvider>
          </DeletionWarningProvider>
        </AuthProvider>
      </ThemeProvider>
    </NotificationProvider>
    <Toaster position="top-right" toastOptions={TOAST_OPTIONS} containerStyle={{ zIndex: 9999 }} />
    {/* Browser validation bubbles in the app's language, for every form without zod (see the component). */}
    <NativeValidationTranslator />
  </QueryClientProvider>
)
